import { apiClient, API_ENDPOINTS } from '../config';
import {
  ApiResponse,
  AnalyticsStats,
  PlayAnalytics,
  UploadAnalytics,
  SystemHealth,
  RecentActivity,
} from '../types';

export class AnalyticsService {
  // Get dashboard statistics
  static async getStats(): Promise<AnalyticsStats> {
    const response = await apiClient.get<ApiResponse<AnalyticsStats>>(API_ENDPOINTS.analytics.stats);
    return response.data.data;
  }

  // Get play analytics
  static async getPlayAnalytics(params: {
    period?: 'day' | 'week' | 'month' | 'year';
    limit?: number;
  } = {}): Promise<PlayAnalytics[]> {
    const response = await apiClient.get<ApiResponse<PlayAnalytics[]>>(
      API_ENDPOINTS.analytics.plays,
      { params }
    );
    return response.data.data;
  }

  // Get upload analytics
  static async getUploadAnalytics(params: {
    period?: 'day' | 'week' | 'month' | 'year';
    start_date?: string;
    end_date?: string;
  } = {}): Promise<UploadAnalytics[]> {
    const response = await apiClient.get<ApiResponse<UploadAnalytics[]>>(
      API_ENDPOINTS.analytics.uploads,
      { params }
    );
    return response.data.data;
  }

  // Get system health
  static async getSystemHealth(): Promise<SystemHealth> {
    const response = await apiClient.get<ApiResponse<SystemHealth>>(API_ENDPOINTS.analytics.system);
    return response.data.data;
  }

  // Get recent activity
  static async getRecentActivity(params: {
    limit?: number;
    type?: string;
    user_id?: string;
  } = {}): Promise<RecentActivity[]> {
    const response = await apiClient.get<ApiResponse<RecentActivity[]>>(
      '/api/analytics/activity',
      { params }
    );
    return response.data.data;
  }

  // Get usage patterns
  static async getUsagePatterns(params: {
    period?: 'day' | 'week' | 'month';
    metric?: 'plays' | 'uploads' | 'users';
  } = {}): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(
      '/api/analytics/usage-patterns',
      { params }
    );
    return response.data.data;
  }

  // Get content performance
  static async getContentPerformance(params: {
    period?: 'day' | 'week' | 'month' | 'year';
    sort_by?: 'plays' | 'rating' | 'engagement';
    limit?: number;
  } = {}): Promise<any[]> {
    const response = await apiClient.get<ApiResponse<any[]>>(
      '/api/analytics/content-performance',
      { params }
    );
    return response.data.data;
  }

  // Get user engagement metrics
  static async getUserEngagement(params: {
    period?: 'day' | 'week' | 'month';
    user_id?: string;
  } = {}): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/analytics/user-engagement',
      { params }
    );
    return response.data.data;
  }

  // Get storage analytics
  static async getStorageAnalytics(): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>('/api/analytics/storage');
    return response.data.data;
  }

  // Get quality metrics
  static async getQualityMetrics(params: {
    period?: 'day' | 'week' | 'month';
    content_type?: string;
  } = {}): Promise<any> {
    const response = await apiClient.get<ApiResponse<any>>(
      '/api/analytics/quality',
      { params }
    );
    return response.data.data;
  }

  // Export analytics data
  static async exportAnalytics(params: {
    type: 'plays' | 'uploads' | 'users' | 'content';
    format: 'csv' | 'json' | 'xlsx';
    period?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<Blob> {
    const response = await apiClient.get('/api/analytics/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  }
}